import React, { useMemo, useState } from "react";
import { Section } from "@design-system/Section/Section";
import { useWorkspaceReceiptSenders } from "@api/workspaceReceiptSender.api";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { CopyIcon } from "@icons";
import { DEFAULT_RECEIPTS_EMAIL } from "@common/constants/domains/receipts/constants";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { ReceiptSendersView } from "./ReceiptSendersView";
import { ReceiptSenderRow } from "./receiptSendersViewConfig";
import { AddReceiptSenderModal } from "./AddReceiptSenderModal";
import { VStack } from "designSystem/VStack/VStack";
import { HStack } from "designSystem/HStack/HStack";

export const ReceiptSendersSection: React.FC = () => {
    const [search, setSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const { data: senders, isLoading } = useWorkspaceReceiptSenders();
    const copyToClipboard = useCopyToClipboard();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const tableData = useMemo<ReceiptSenderRow[] | null>(() => {
        if (isLoading) {
            return null;
        }
        if (!senders) {
            return [];
        }
        return senders.filter(
            (sender) =>
                sender.email &&
                sender.email.toLowerCase().includes(search.toLowerCase()),
        );
    }, [senders, isLoading, search]);

    const receiptsEmailButton = (
        <Button
            variant="primary"
            rightIcon={<CopyIcon />}
            onClick={() => copyToClipboard(DEFAULT_RECEIPTS_EMAIL)}
        >
            {DEFAULT_RECEIPTS_EMAIL}
        </Button>
    );

    return (
        <Section
            heading="Admin forwarding address"
            subheading={
                <VStack gap={12}>
                    <HStack gap={8}>{receiptsEmailButton}</HStack>
                    Securely forward receipts, invoices, bills and other
                    financial documents from any approved addresses listed
                    below.
                </VStack>
            }
        >
            <TablePageHeader search={search} onSearchChange={setSearch}>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                        // Refusal must happen here: submit is too late for this modal.
                        if (refuseIfViewOnly()) {
                            return;
                        }
                        setShowAddModal(true);
                    }}
                >
                    Add email
                </Button>
            </TablePageHeader>
            <ReceiptSendersView data={tableData} />

            <AddReceiptSenderModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
            />
        </Section>
    );
};
